import Accordion from "@/common/components/Accordion";
import Button from "@/common/components/Button";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import MaintenanceObjective from "@/common/components/MaintenanceObjective";
import ResumeItem from "@/common/components/ResumeItem";
import { cn } from "@/common/utils/cn";
import { MaintenanceDirectiveObjectiveData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type";
import { CameraIcon } from "lucide-react";
import ExecutionEntryObjectiveActivities from "../ExecutionEntryDiagnostic/ExecutionEntryObjectiveActivities";
import ExecutionEntryObjectivePieces from "../ExecutionEntryDiagnostic/ExecutionEntryObjectivePieces";
import ExecutionObjectiveComplete from "./ExecutionObjectiveComplete";

interface ExecutionObjectiveItemProps {
  objective: MaintenanceDirectiveObjectiveData;
  isInProgress: boolean;
}

const ExecutionObjectiveItem = ({
  objective,
  isInProgress,
}: ExecutionObjectiveItemProps) => {
  const isDisapproved = objective.last_review?.state === 'disapproved';

  return (
    <Accordion
      key={objective.id}
      defaultOpen
      className={cn(
        'p-0 rounded-lg',
        objective.status === 'complete' && isInProgress && !isDisapproved && 'bg-success-op',
        isDisapproved && 'bg-yellow-op-2',
      )}
      triggerClassName="p-0 pr-4 bg-white-100 rounded-t-lg"
      bodyClassName="p-4"
      head={(
        <div className="flex items-center flex-wrap gap-4 w-full pr-4">
          <div className="flex items-start flex-wrap gap-4">
            <div onClick={(e) => e.stopPropagation()}>
              <ImageCarousel
                files={objective.photos?.map((photo) => ({
                  id: photo.id,
                  file: photo.url,
                }))}
                Placeholder={(
                  <ImageWithPlaceholder
                    className={cn(
                      'size-28 rounded-md',
                      isDisapproved && 'size-40',
                    )}
                    iconClassName="size-14"
                    placeholderIcon={CameraIcon}
                  />
                )}
                className={cn(
                  'size-28',
                  isDisapproved && 'size-40',
                )}
                showExpandButton
                showRemoveButton={false}
              />
            </div>

            <div className="flex flex-col items-start gap-2 py-3">
              <MaintenanceObjective
                systemDisplayName={objective.system.display_name}
                actionDisplayName={objective.action.display_name}
                partDisplayName={objective.part.display_name}
                actionDescription={objective.action.description}
              />

              {isDisapproved && (
                <ResumeItem
                  containerClassName="items-start"
                  className="text-xs"
                  label="Justificativa"
                  value={objective?.last_review?.description || '--'}
                />
              )}
            </div>
          </div>

          <div
            className="ml-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {(objective.status !== 'complete' || isDisapproved) && isInProgress && (
              <ExecutionObjectiveComplete
                objective={objective}
              />
            )}

            {objective.status === 'complete' && !isDisapproved && isInProgress && (
              <Button variant="neutral">
                Finalizado
              </Button>
            )}
          </div>
        </div>
      )}
      body={(
        <div className="flex flex-col gap-3">
          <ExecutionEntryObjectivePieces
            objective={objective}
          />

          <ExecutionEntryObjectiveActivities
            objective={objective}
          />
        </div>
      )}
    />
  );
}

export default ExecutionObjectiveItem;
